/**
 * main.ts — wire it together.
 *
 * Construct the Engine, attach audio + render, run the requestAnimationFrame loop
 * (calling engine.tick(dt)), and let the WeatherSource push snapshots in. The
 * Begin overlay performs the Web-Audio user-gesture unlock.
 *
 * No chime logic lives here — the engine owns all of it. This file only feeds
 * weather, renders the view state, and plays the note stream.
 */
import './style.css';
import { Engine } from '@windchime/engine';
import { DEFAULT_PARAMS } from '@windchime/shared';
import type { EngineParams, GeocodeResult } from '@windchime/shared';
import { ChimeAudio } from './audio';
import { Renderer } from './render';
import { WeatherSource, haversineKm } from './weather';
import { UI } from './ui';
import type { SearchItem } from './ui';

const engine = new Engine(); // defaults: E♭ Ionian, mode_auto, 6 notes in one octave
// pitch ±: the chime starts in its middle octave and moves at most one octave either way
let octaveOffset = 0;
const audio = new ChimeAudio();
const canvas = document.getElementById('cv') as HTMLCanvasElement;
const renderer = new Renderer(canvas);
const weather = new WeatherSource(engine, import.meta.env.VITE_GATEWAY_URL);

const ui = new UI({
  onBegin: begin,
  onPlayToggle: togglePlay,
  onSearch: doSearch,
  onQueryChange: (q) => void doTypeahead(q),
  onClearLocation: () =>
    weather.startSimulated(
      weather.gatewayConfigured
        ? undefined // the search placeholder carries the prompt
        : 'No gateway configured — running on simulated wind.',
    ),
  onKey: (pc) => engine.setParams({ rootPc: pc }),
  onOctave: (delta) => {
    const next = Math.max(-1, Math.min(1, octaveOffset + delta));
    if (next !== octaveOffset) {
      octaveOffset = next;
      engine.setParams({ rootOctave: DEFAULT_PARAMS.rootOctave + octaveOffset });
      ui.setPitchState(octaveOffset);
    }
  },
  onMode: (name) => engine.setModeOverride(name),
  onRingToggle: (show) => {
    renderer.showRing = show;
  },
  onStrikeModel: (contact) => engine.setParams({ strikeModel: contact ? 'contact' : 'poisson' }),
  onPhysics: (patch) => engine.setParams(patch as Partial<EngineParams>),
});

// every engine note -> glockenspiel synth (the Tier A seam)
engine.onNote((e) => {
  audio.strike(e.midi, e.velocity);
});

// weather/source changes -> readout chrome
weather.onStatus((s) => ui.setSource(s));
ui.setPitchState(octaveOffset); // seed the pitch stepper (middle octave: both directions open)

window.addEventListener('resize', () => renderer.resize());
document.addEventListener('visibilitychange', () => {
  if (!document.hidden && playing) audio.resume();
});

// seed the engine + readout with weather before Begin is pressed
weather.startSimulated(
  weather.gatewayConfigured
    ? undefined // the search placeholder carries the prompt
    : 'No gateway configured — running on simulated wind.',
);

let running = false;
let playing = true;
let last = 0;
let roAcc = 0; // readout throttle accumulator

function begin(): void {
  audio.init(); // audio context is created + resumed inside the Begin click (user-gesture unlock)
  audio.resume();
  renderer.resize(); // size the canvas before it reveals so the first frame is correct
  playing = true;
  ui.setPlaying(true);
  startLoop();
  ui.playIntro(); // fly the intro out, then reveal the main UI in a staggered order
}

function startLoop(): void {
  if (running) return;
  running = true;
  last = performance.now();
  requestAnimationFrame(loop);
}

function togglePlay(): void {
  playing = !playing;
  ui.setPlaying(playing);
  if (playing) {
    audio.resume();
    startLoop();
  } else {
    audio.suspend();
    running = false; // loop bails on its next frame
  }
}

function loop(ts: number): void {
  if (!running) return;
  const dt = Math.min(0.05, (ts - last) / 1000 || 0.016);
  last = ts;

  weather.frame(dt); // simulated drift pushes setWeather (no-op in live mode)
  engine.tick(dt); // advances sim; fires notes -> onNote -> audio + feed
  const vs = engine.getViewState();
  renderer.draw(vs, weather.renderWeather(), dt);

  roAcc += dt;
  if (roAcc >= 0.15) {
    roAcc = 0;
    const s = weather.snapshot;
    ui.setReadout({
      rootPc: vs.rootPc,
      modeName: vs.mode.name,
      windSpeed: s.windSpeed,
      windDirFrom: s.windDirFrom,
      gust: s.gust,
      tempF: s.tempF,
      localHour: s.localHour,
      activity: vs.activity,
    });
  }
  requestAnimationFrame(loop);
}

/** Attach distances (when a location is already selected), sort nearest-first, and
 *  keep only the 3 best — we still fetch 8 candidates so the proximity sort has
 *  enough material to surface the truly nearest ones. */
function toItems(results: GeocodeResult[]): SearchItem[] {
  const ref = weather.referencePoint;
  if (!ref) return results.slice(0, 3).map((place) => ({ place }));
  return results
    .map((place) => ({ place, distanceKm: haversineKm(ref.lat, ref.lon, place.lat, place.lon) }))
    .sort((a, b) => a.distanceKm - b.distanceKm)
    .slice(0, 3);
}

function pickPlace(r: GeocodeResult): void {
  weather.selectPlace(r).catch((err) => ui.setHint(String(err?.message ?? err), true));
}

// type-ahead bookkeeping: abort superseded fetches; drop out-of-order responses
let searchSeq = 0;
let searchAbort: AbortController | null = null;

async function doTypeahead(q: string): Promise<void> {
  if (!weather.gatewayConfigured) return; // demo mode — typing shouldn't spam warnings
  searchAbort?.abort();
  const ab = new AbortController();
  searchAbort = ab;
  const seq = ++searchSeq;
  try {
    const results = await weather.geocode(q, ab.signal);
    if (seq !== searchSeq) return; // a newer keystroke superseded this response
    ui.showResults(toItems(results), pickPlace);
  } catch {
    // best-effort: aborted/failed type-ahead stays silent (submit still reports errors)
  }
}

async function doSearch(q: string): Promise<void> {
  searchAbort?.abort();
  const seq = ++searchSeq; // an explicit search supersedes any in-flight type-ahead
  try {
    const results = await weather.geocode(q);
    if (seq !== searchSeq) return;
    ui.showResults(toItems(results), pickPlace);
  } catch (err) {
    ui.clearResults();
    ui.setHint(
      weather.gatewayConfigured
        ? `Search failed: ${String((err as Error)?.message ?? err)}`
        : 'No gateway configured — set VITE_GATEWAY_URL for live weather.',
      true,
    );
  }
}

// PWA: register the service worker in production only (dev stays uncached for HMR)
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}
